import React, { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { UserContext } from '../../../App'
import API from '../../../services/API'

// Best-effort page name from the path: last segment that isn't an id,
// kebab/camel split, title-cased. The user can edit it before saving.
const guessLabel = (pathname) => {
  const segments = pathname
    .split('/')
    .filter(Boolean)
    .filter((segment) => !/^[a-f0-9]{24}$/i.test(segment) && !/^\d+$/.test(segment))
  const last = segments[segments.length - 1] ?? 'Home'
  return last
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const FavouritesBar = () => {
  const { user, setUser } = useContext(UserContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [addAnchor, setAddAnchor] = useState(null)
  const [label, setLabel] = useState('')
  const [saving, setSaving] = useState(false)

  const favourites = user?.uiPreferences?.favouritePages ?? []
  const currentFavourite = favourites.find((fav) => fav.path === location.pathname)

  const save = (next) => {
    setSaving(true)
    return API.post('/account/uipreferences', { favouritePages: next })
      .then((res) => {
        if (res.status === 200) {
          setUser((prev) => ({ ...prev, uiPreferences: res.data }))
        }
      })
      .finally(() => setSaving(false))
  }

  const handleAdd = () => {
    const trimmed = label.trim()
    if (!trimmed) return
    save([...favourites, { label: trimmed, path: location.pathname }]).then(() =>
      setAddAnchor(null),
    )
  }

  const handleRemove = (path) => {
    if (saving) return
    save(favourites.filter((fav) => fav.path !== path))
  }

  if (!user?._id) return null

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.25}
      px={1.5}
      py={0.75}
      bgcolor="background.paper"
      borderBottom="1px solid"
      borderColor="divider"
    >
      <Tooltip title={currentFavourite ? '' : 'Add this page to favourites'}>
        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
          <ButtonBase
            disabled={!!currentFavourite || saving}
            onClick={(e) => {
              setLabel(guessLabel(location.pathname))
              setAddAnchor(e.currentTarget)
            }}
            sx={{
              width: 26,
              height: 26,
              borderRadius: '8px',
              bgcolor: 'rgba(204, 164, 59, 0.14)',
              transition: 'background-color 120ms ease',
              '& .fav-add-icon': { display: 'none' },
              ...(!currentFavourite && {
                '&:hover': {
                  bgcolor: 'rgba(35, 154, 164, 0.12)',
                  '& .fav-star-icon': { display: 'none' },
                  '& .fav-add-icon': { display: 'inline-block' },
                },
              }),
            }}
          >
            <StarRoundedIcon
              className="fav-star-icon"
              sx={{ color: 'highlight.main', fontSize: 17 }}
            />
            <BookmarkAddOutlinedIcon
              className="fav-add-icon"
              sx={{ color: 'contrast.main', fontSize: 16 }}
            />
          </ButtonBase>
        </span>
      </Tooltip>
      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        flex={1}
        minWidth={0}
        overflow="auto"
        sx={{ whiteSpace: 'nowrap', scrollbarWidth: 'none' }}
      >
        {favourites.length ? (
          favourites.map((fav) => {
            const active = fav.path === location.pathname
            return (
              <Chip
                key={fav.path}
                size="small"
                label={fav.label}
                onClick={() => navigate(fav.path)}
                onDelete={() => handleRemove(fav.path)}
                deleteIcon={
                  <Tooltip title="Remove from favourites">
                    <CloseRoundedIcon />
                  </Tooltip>
                }
                sx={{
                  flexShrink: 0,
                  height: 26,
                  borderRadius: '999px',
                  fontWeight: 600,
                  fontSize: 12,
                  transition: 'background-color 120ms ease',
                  ...(active
                    ? {
                        bgcolor: 'rgba(35, 154, 164, 0.12)',
                        color: 'contrast.dark',
                        '&:hover': { bgcolor: 'rgba(35, 154, 164, 0.2)' },
                      }
                    : {
                        bgcolor: 'anMuted.main',
                        color: 'text.primary',
                        '&:hover': { bgcolor: 'greycontrast.main' },
                      }),
                  '& .MuiChip-deleteIcon': {
                    fontSize: 15,
                    width: 0,
                    margin: 0,
                    opacity: 0,
                    overflow: 'hidden',
                    transition: 'width 120ms ease, opacity 120ms ease',
                    color: active ? 'contrast.dark' : 'text.secondary',
                    '&:hover': { color: 'anRed.main' },
                  },
                  '&:hover .MuiChip-deleteIcon, &:focus-within .MuiChip-deleteIcon': {
                    width: 15,
                    marginRight: '6px',
                    marginLeft: '-4px',
                    opacity: 1,
                  },
                }}
              />
            )
          })
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Pin your favourite pages here for one-click access.
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Pin favourite pages here.
            </Box>
          </Typography>
        )}
      </Box>
      {!currentFavourite && (
        <Tooltip title="Add this page to favourites">
          <span>
            <IconButton
              size="small"
              disabled={saving}
              onClick={(e) => {
                setLabel(guessLabel(location.pathname))
                setAddAnchor(e.currentTarget)
              }}
              sx={{
                flexShrink: 0,
                width: 26,
                height: 26,
                borderRadius: '8px',
                color: 'contrast.main',
                bgcolor: 'anMuted.main',
                transition: 'background-color 120ms ease',
                '&:hover': { bgcolor: 'greycontrast.main' },
              }}
            >
              <BookmarkAddOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Popover
        open={!!addAnchor}
        anchorEl={addAnchor}
        onClose={() => setAddAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.5 } } }}
      >
        <Stack p={1.5} gap={1} width={260}>
          <Typography variant="subtitle2">Add to favourites</Typography>
          <TextField
            size="small"
            label="Favourite name"
            value={label}
            autoFocus
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <Button
            variant="contained"
            size="small"
            color="contrast"
            onClick={handleAdd}
            disabled={saving || !label.trim()}
          >
            Add to favourites
          </Button>
        </Stack>
      </Popover>
    </Box>
  )
}

export default FavouritesBar
